/**
 * `message_agent_blocked` — customers are writing into a message-agent channel and the agent
 * cannot answer, for a reason only the workspace can clear.
 *
 * Distinct from `paused_conversation`, which reports one conversation waiting on a human: this
 * one reports that the agent is not answering *anyone*. Carries no message body — the alert says
 * what is blocked and who must act, which keeps the renderers clear of any PII decision.
 */

import type { TenantEventDefinition } from './definition'

export const MESSAGE_AGENT_BLOCKED_EVENT_TYPE = 'message_agent_blocked' as const

/**
 * The four causes, and the throttle keys off this value. The first two mirror
 * `AiAccessBlockedError.reason` literally so the emitter can forward it without a mapping.
 */
export type MessageAgentBlockedReason =
  | 'balance_depleted'
  | 'spending_cap'
  | 'agent_disabled'
  | 'tenant_inactive'

export interface MessageAgentBlockedPayload {
  agentId: string
  agentName?: string
  /** Channel the customer wrote on, for "where is this happening" context. */
  channelType: string
  reason: MessageAgentBlockedReason
  /** Absent only when the block was caught before a conversation existed (rare fallbacks). */
  conversationId?: string
}

const REASONS: readonly MessageAgentBlockedReason[] = [
  'balance_depleted',
  'spending_cap',
  'agent_disabled',
  'tenant_inactive',
]

function isMessageAgentBlockedPayload(payload: unknown): payload is MessageAgentBlockedPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.agentId === 'string' &&
    typeof p.channelType === 'string' &&
    REASONS.includes(p.reason as MessageAgentBlockedReason)
  )
}

export const messageAgentBlockedEventDefinition: TenantEventDefinition<MessageAgentBlockedPayload> =
  {
    eventType: MESSAGE_AGENT_BLOCKED_EVENT_TYPE,
    supportedChannelTypes: ['email', 'telegram'],
    validatePayload: isMessageAgentBlockedPayload,
    tenant: {
      label: 'Message agent not replying',
      description: 'Customers are writing, but a message agent cannot answer: it is off, the balance is depleted, a spending limit was reached, or the tenant is inactive.',
      recipients: 'account_alerts',
      mandatory: false,
    },
  }
